import api from '@api';
import ChatError from '@components/layout/main/ChatError';
import ChatMessage from '@components/layout/main/ChatMessage';
import Header from '@components/layout/main/Header';
import Styled from '@components/layout/main/Main.styled';
import Prompt from '@components/layout/main/Prompt';
import PromptShowcase from '@components/layout/main/PromptShowcase';
import QuestionPanel from '@components/layout/main/QuestionPanel';
import SourceInfoDialog from '@components/layout/main/SourceInfoDialog';
import { AudienceType, Log, MessageType } from '@enums';
import { useEventSourceChat, useTouchDevice, useWindowSize } from '@hooks';
import { useChatStore } from '@store/chat';
import { useProfileStore } from '@store/profile';
import { useShellStore } from '@store/shell';
import { CircularLoader } from '@ui/loaders';
import * as utils from '@utils';
import {
  Fragment,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { Analytics } from 'types/analytics';
import type { Message } from 'types/common';
import type { Dataset, PromptUsage, RelevantAudience } from 'types/schema';

function Main() {
  const chatStore = useChatStore();
  const { user } = useProfileStore();
  const shellStore = useShellStore();
  const currentPromptMessage = chatStore.getCurrentPromptMessage();
  const chat = useEventSourceChat(currentPromptMessage, chatStore.chat.id);
  const windowSize = useWindowSize();
  const isTouchDevice = useTouchDevice();
  const mainRef = useRef<HTMLElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const echoRef = useRef<HTMLDivElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const reasoningRef = useRef({ duration: 0, count: 0 });
  const transientMessageIndexRef = useRef(0);
  const transientMessageMapRef = useRef(new WeakMap<Message, number>());
  const submittedPromptAnalyticsData = useRef<Analytics.EventProps.SentMessage>(
    {} as Analytics.EventProps.SentMessage
  );
  const [quota, setQuota] = useState<PromptUsage>({ ['usage_remaining']: -1 });
  const [currentDataset, setCurrentDataset] = useState<
    Dataset & { hasChanged: boolean }
  >({ ...chatStore.selectedDataset, hasChanged: false });
  const [chatViewportMetrics, setChatViewportMetrics] = useState({
    usedHeight: 0,
    viewportHeight: 0
  });
  const [connectionErrorCount, setConnectionErrorCount] = useState(0);
  const [naturalLanguageErrorCount, setNaturalLanguageErrorCount] = useState(0);
  const messageCount = chatStore.getMessageCount();

  // (A) Scroll to bottom on new System/User message
  useEffect(() => {
    const messages = chatStore.chat.messages;
    const latestMessage = messages[messages.length - 1];

    if (
      latestMessage?.['message_type'] === MessageType.System ||
      latestMessage?.['message_type'] === MessageType.User
    ) {
      mainRef.current?.scrollTo({
        top: mainRef.current?.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chatStore.chat.messages]);

  // (B) Scroll to bottom on connection error
  useEffect(() => {
    if (connectionErrorCount === 1) {
      window.requestAnimationFrame(() =>
        mainRef.current?.scrollTo({ top: mainRef.current?.scrollHeight })
      );
    }
  }, [connectionErrorCount]);

  // (C) Scroll to bottom on resume
  useEffect(() => {
    if (chatStore.isResumed) {
      // IMPORTANT: It is crucial to reset this flag
      chatStore.setIsResumed(false);

      window.setTimeout(() => {
        if (!isTouchDevice) {
          promptRef.current?.focus();
        }
        mainRef.current?.scrollTo({
          top: mainRef.current?.scrollHeight,
          behavior: 'smooth'
        });
      }, 0);
    }
  }, [chatStore.isResumed, chatStore.setIsResumed, isTouchDevice]);

  useEffect(() => {
    if (!currentPromptMessage.prompt || echoRef.current === null) {
      return;
    }

    const isQuestionPanelOpen = shellStore.isQuestionPanelOpen;
    const getHeight = (element?: HTMLElement | null) =>
      element?.offsetHeight ?? 0;
    const getY = (element?: Element | null) =>
      element?.getBoundingClientRect().y ?? 0;

    const echoHeight = getHeight(
      echoRef.current.querySelector<HTMLElement>('.echo')
    );
    const actionPanelHeight = getHeight(
      document.querySelector<HTMLElement>('.action-panel')
    );
    const mainHeight = getHeight(mainRef.current);
    const navbarHeight = window.innerHeight - mainHeight - actionPanelHeight;
    const viewportHeight =
      getY(inputAreaRef.current) - navbarHeight - actionPanelHeight;

    if (import.meta.env.DEV) {
      utils.log(
        `Chat area available viewport height: ${viewportHeight} (question panel: ${isQuestionPanelOpen})`,
        Log.Debug
      );
    }

    setChatViewportMetrics({
      usedHeight: echoHeight,
      viewportHeight
    });
  }, [currentPromptMessage.prompt, shellStore.isQuestionPanelOpen]);

  const handlePrompt = useCallback(
    (
      message: string,
      method: 'enter' | 'click' | 'template_query' | 'home' | 'retry',
      audiences: RelevantAudience[],
      isRetry = false
    ) => {
      if (!message?.trim()) {
        return;
      }

      // Display loader
      chatStore.setIsLoading(true);

      // Reset the reopened chat flag
      chatStore.setIsResumed(false);

      // Prepare the analytics event data
      submittedPromptAnalyticsData.current = {
        ...submittedPromptAnalyticsData.current,
        ['dataset_details']: currentDataset.title,
        method,
        ['nb_total_added_audience']: audiences.length,
        ['nb_ai_audiences']: audiences.filter(
          ({ type }) => type === AudienceType.InstantAudience
        ).length,
        ['nb_gwi_audience']: audiences.filter(
          ({ type }) => type === AudienceType.GWIAudience
        ).length,
        ['nb_my_audience']: audiences.filter(
          ({ type }) => type === AudienceType.AuthoredAudience
        ).length,
        ['nb_shared_audience']: audiences.filter(
          ({ type }) => type === AudienceType.SharedAudience
        ).length,
        ['query']: message,
        ['query_ranking']: chatStore.getPromptMessageCount() + 1
      };

      if (isRetry) {
        chatStore.pushMessage(
          {
            prompt: message,
            ['message_type']: MessageType.User
          },
          true
        );

        return;
      }

      if (currentDataset.hasChanged && chatStore.getMessageCount() > 1) {
        startTransition(() => {
          utils.log('Pushing system message to chat area.', Log.Notice);
          chatStore.pushMessage({
            ['message_type']: MessageType.System,
            ['system_text']: `Data set switched to "${currentDataset.title}"`
          });
          // Reset flag
          setCurrentDataset((currentDataset) => ({
            ...currentDataset,
            hasChanged: false
          }));
        });
      }

      // Echo user's input to chat area
      chatStore.pushMessage({
        prompt: message,
        ['message_type']: MessageType.User
      });
    },
    [
      currentDataset,
      chatStore.getMessageCount,
      chatStore.getPromptMessageCount,
      chatStore.setIsResumed,
      chatStore.pushMessage,
      chatStore.setIsLoading
    ]
  );

  const onUserPrompt = useCallback(
    (message: string) => {
      handlePrompt(message, 'click', useChatStore.getState().selectedAudiences);
    },
    [handlePrompt]
  );

  const handleRetry = useCallback(() => {
    if (shellStore.isOffline) {
      shellStore.setToast({
        message: 'No internet connection',
        status: 'error'
      });
      setConnectionErrorCount((count) => count + 1);
    } else {
      handlePrompt(
        currentPromptMessage.prompt,
        'click',
        chatStore.selectedAudiences,
        true
      );
    }
  }, [
    currentPromptMessage.prompt,
    chatStore.selectedAudiences,
    shellStore.isOffline,
    shellStore.setToast,
    handlePrompt
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    if (shellStore.isOffline) {
      setConnectionErrorCount(1);
    } else {
      setConnectionErrorCount(0);
      setNaturalLanguageErrorCount(0);
      handleRetry();
    }
    /*
     * Note: handleRetry is intentionally excluded from the dependency array
     * to prevent this effect from re-running whenever its dependencies
     * (such as prompt or audiences) change.
     */
  }, [shellStore.isOffline]);

  useEffect(() => {
    if (chat.serverError === null) {
      return;
    }

    if (chat.serverError.type === 'natural_language') {
      setNaturalLanguageErrorCount((count) => count + 1);
    } else {
      shellStore.setToast({
        message: 'Sorry, something went wrong',
        status: 'error'
      });
    }

    // Clear the thinking and loading messages
    chatStore.clearTransientMessages();

    // Reset track data
    reasoningRef.current.count = 0;
    reasoningRef.current.duration = 0;
    submittedPromptAnalyticsData.current = null;
  }, [chat.serverError, chatStore.clearTransientMessages, shellStore.setToast]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    if (!chat.incomingMessage) {
      return;
    }

    const { metadata } = chat.incomingMessage;
    const chatId = metadata['chat_id'];

    if (
      // The chat ID is present
      chatId &&
      // The chat ID is different from the one in the URL
      chatId !== utils.getPathnameId() &&
      // The chat has only one message
      messageCount === 1
    ) {
      utils.updatePathname(shellStore.pathname, chatId);

      // Update the current chat in the store
      chatStore.setCurrentChat({ id: chatId });

      // Reset the natural language error count
      setNaturalLanguageErrorCount(0);
    }
  }, [chat.incomingMessage, messageCount, shellStore.pathname]);

  const trackCompletedReasoning = useCallback(
    (isThinking: boolean, chatId: string) => {
      if (isThinking) {
        reasoningRef.current.count++;

        if (reasoningRef.current.duration === 0) {
          reasoningRef.current.duration = Date.now();
        }
      } else if (reasoningRef.current.count > 0) {
        const duration = Date.now() - reasoningRef.current.duration;

        api.analytics.completedReasoning({
          ['chat_id']: chatId,
          ['nb_duration_secs']: Number((duration / 1000).toFixed(1)),
          ['nb_steps']: reasoningRef.current.count
        });

        reasoningRef.current.duration = 0;
        reasoningRef.current.count = 0;
      }
    },
    []
  );

  useEffect(() => {
    if (!chat.incomingMessage) {
      return;
    }

    const { metadata } = chat.incomingMessage;
    const messageType = metadata['response_message_type'] as MessageType;
    const isSupported = [
      MessageType.Loading,
      MessageType.NaturalLanguage,
      MessageType.AgentThinking
    ].includes(messageType);

    if (!isSupported) {
      return;
    }

    utils.log(`✨ %cSpark %c(%s)`, {
      custom: [
        'color: rgb(222, 27, 118)',
        'color: rgb(84, 97, 200)',
        messageType,
        chat.incomingMessage
      ]
    });

    trackCompletedReasoning(
      messageType === MessageType.AgentThinking,
      metadata['chat_id']
    );

    startTransition(() => {
      // Post message to chat area
      chatStore.pushMessage(chat.incomingMessage);
    });

    if (messageType !== MessageType.NaturalLanguage) {
      return;
    }

    if (submittedPromptAnalyticsData.current) {
      submittedPromptAnalyticsData.current['chat_id'] = metadata['chat_id'];
      submittedPromptAnalyticsData.current['nb_sources'] = utils.countSources(
        chat.incomingMessage['natural_language_response']
      );
      api.analytics.sentMessage(submittedPromptAnalyticsData.current);
      submittedPromptAnalyticsData.current = null;
    }

    const questions = utils.parseQuestionPanelUrl(
      chat.incomingMessage['natural_language_response']
    );

    if (questions) {
      chatStore.setQuestionPanelData(questions);
      shellStore.setIsQuestionPanelOpen(true);
    }
  }, [
    chat.incomingMessage,
    chatStore.pushMessage,
    chatStore.setQuestionPanelData,
    shellStore.setIsQuestionPanelOpen,
    trackCompletedReasoning
  ]);

  useEffect(() => {
    if (chat.isReplyCompleted) {
      // Hide the loader if the reply is complete
      chatStore.setIsLoading(false);
      // Fetch the updated quota
      api.user
        .getPromptUsage()
        .then((quota) => startTransition(() => setQuota(quota)));
    }
  }, [chat.isReplyCompleted, chatStore.setIsLoading]);

  useEffect(() => {
    if (typeof isTouchDevice === 'undefined' || isTouchDevice) {
      return;
    }

    if (!(chatStore.isLoading || currentDataset.hasChanged)) {
      promptRef.current?.focus();
    }
  }, [chatStore.isLoading, currentDataset.hasChanged, isTouchDevice]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    api.user.getPromptUsage().then((quota) => {
      utils.log(
        `Quota: ${
          quota['usage_remaining'] === -1
            ? '*UNLIMITED*'
            : quota['usage_remaining']
        }`
      );
      setQuota(quota);
    });

    // Reset everything when the component is unmounted
    return () => {
      chatStore.clear();
      shellStore.reset();
    };
  }, []);

  const handleChangeDataset = useCallback(
    (dataset: Dataset) => {
      api.analytics.selectedDataset({
        ['dataset_before']: currentDataset.title,
        ['dataset_after']: dataset.title
      });

      // Add a system message to the chat if there are messages
      setCurrentDataset({
        ...dataset,
        hasChanged: true
      });

      utils.log(`Dataset switched to "${dataset.title}".`);
    },
    [currentDataset]
  );

  const handleCancelReply = useCallback(() => {
    // Cancel the reply by reseting the web socket connection
    chat.cancelReply();

    // Hide the loader
    chatStore.setIsLoading(false);

    // Clear the thinking and loading messages
    chatStore.clearTransientMessages();

    utils.log('Aborted current response.', Log.Notice);

    // Reset track data
    reasoningRef.current.count = 0;
    reasoningRef.current.duration = 0;
  }, [
    chat.cancelReply,
    chatStore.setIsLoading,
    chatStore.clearTransientMessages
  ]);

  const handleCloseSourceInfoDialog = useCallback(() => {
    shellStore.setIsSourceInfoDialogOpen(false);
  }, [shellStore.setIsSourceInfoDialogOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  const memoizedChat = useMemo(
    () =>
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: OK
      chatStore.chat.messages.map((message, index) => {
        if (message['message_type'] === MessageType.System) {
          return (
            <Styled.SystemText key={`system-message-${index}`}>
              {message['system_text']}
            </Styled.SystemText>
          );
        }

        const { messageId, messageType } = getMessageMetadata(message);
        const isAgentLoading = messageType === MessageType.Loading;
        const isAgentThinking = messageType === MessageType.AgentThinking;

        if ('prompt' in message) {
          // Fresh user message
          return (
            <ChatMessage
              key={`user-message-${index + 1}`}
              viewportHeight={chatViewportMetrics.viewportHeight}
              ref={echoRef}
              user={user}>
              {message['prompt']}
            </ChatMessage>
          );
        } else if ('message' in message) {
          // User message from the stored chat
          return (
            <ChatMessage
              key={`user-message-${messageId}`}
              viewportHeight={chatViewportMetrics.viewportHeight}
              ref={echoRef}
              user={user}>
              {message['message']}
            </ChatMessage>
          );
        } else {
          const hasSources = message['sources_available'];
          const { viewportHeight, usedHeight } = chatViewportMetrics;
          const isAgentActive = isAgentLoading || isAgentThinking;
          const isLastMessage = index === chatStore.chat.messages.length - 1;

          let key = `agent-reply-${messageId}`;

          if (isAgentActive) {
            const thinkingMessageMap = transientMessageMapRef.current;
            let transientIndex = thinkingMessageMap.get(message);

            if (transientIndex === undefined) {
              transientIndex = ++transientMessageIndexRef.current;
              thinkingMessageMap.set(message, transientIndex);
            }

            key = `agent-thinking-${messageId}-${transientIndex}`;
          } else {
            transientMessageMapRef.current.delete(message);
          }

          return (
            <ChatMessage
              className={utils.getClassName({
                ['pop-in']: isAgentActive,
                ['transient']: isAgentActive
              })}
              id={messageId}
              key={key}
              viewportHeight={viewportHeight - usedHeight}
              onUserPrompt={onUserPrompt}
              isLastMessage={isLastMessage}
              shouldShowThinkingOverview={!isAgentActive && hasSources}
              shouldShowFeedback={!isAgentActive}
              shouldStreamText={
                isLastMessage && !isAgentThinking && !chatStore.isResumed
              }
              onHover={() => {
                if (useChatStore.getState().hoveredAgentReplyIndex !== index) {
                  useChatStore.setState({ hoveredAgentReplyIndex: index });

                  if (import.meta.env.DEV) {
                    utils.log(`Current agent reply has index ${index}`, {
                      type: Log.Debug
                    });
                  }
                }
              }}>
              {message['natural_language_response']}
            </ChatMessage>
          );
        }
      }),
    [chatStore.chat.messages, user, chatViewportMetrics, onUserPrompt]
  );

  const finePrint = (
    <Styled.FinePrint>
      AI can make mistakes.{' '}
      <span style={{ whiteSpace: 'nowrap' }}>
        Consider checking important information.
      </span>
    </Styled.FinePrint>
  );

  const content = useMemo(
    () =>
      !chatStore.hasStartedChat() ? (
        <Fragment>
          <Header />
          <Prompt
            autoFocus={windowSize.isLarge}
            shouldAnimateQuotaStatus
            quota={quota}
            ref={promptRef}
            isLoading={chatStore.isLoading}
            onCancel={handleCancelReply}
            onChangeDataset={handleChangeDataset}
            onSubmit={handlePrompt}
          />
          {finePrint}
          <Styled.Separator />
          <PromptShowcase
            disabled={quota['usage_remaining'] === 0}
            onSelectPrompt={({ prompt }) =>
              handlePrompt(
                prompt,
                'template_query',
                chatStore.selectedAudiences
              )
            }
          />
        </Fragment>
      ) : (
        <Fragment>
          <Styled.ChatArea ref={chatAreaRef} className="chat-area">
            {memoizedChat}
            <ChatError
              connectionErrorCount={connectionErrorCount}
              naturalLanguageErrorCount={naturalLanguageErrorCount}
              onRetry={handleRetry}
            />
          </Styled.ChatArea>
          <Styled.InputArea ref={inputAreaRef}>
            {shellStore.isQuestionPanelOpen ? (
              <QuestionPanel
                onSubmit={(answer) =>
                  handlePrompt(answer, 'click', chatStore.selectedAudiences)
                }
              />
            ) : (
              <Styled.Prompt>
                <Prompt
                  ref={promptRef}
                  quota={quota}
                  isLoading={chatStore.isLoading}
                  onCancel={handleCancelReply}
                  onChangeDataset={handleChangeDataset}
                  onSubmit={handlePrompt}
                />
              </Styled.Prompt>
            )}
            {finePrint}
          </Styled.InputArea>
        </Fragment>
      ),
    [
      chatStore.hasStartedChat,
      chatStore.isLoading,
      chatStore.selectedAudiences,
      memoizedChat,
      connectionErrorCount,
      naturalLanguageErrorCount,
      quota,
      shellStore.isQuestionPanelOpen,
      windowSize.isLarge,
      handleCancelReply,
      handleChangeDataset,
      handlePrompt,
      handleRetry
    ]
  );

  return (
    <Styled.Main
      className="overlay-scrollbar"
      id="main"
      isCompact={shellStore.isRightPanelOpen}
      ref={mainRef}>
      <Styled.Stage>
        {chatStore.sessionIsLoading ? (
          <Styled.LoaderWrapper>
            <CircularLoader selectedSize="M" />
          </Styled.LoaderWrapper>
        ) : (
          content
        )}
      </Styled.Stage>
      <SourceInfoDialog
        open={shellStore.isSourceInfoDialogOpen}
        onClose={handleCloseSourceInfoDialog}
      />
    </Styled.Main>
  );
}

function getMessageMetadata(message: Message) {
  if ('metadata' in message) {
    const { metadata } = message;
    const messageId = metadata['message_id'];
    const messageType = metadata['response_message_type'];

    return { messageId, messageType };
  }

  return {
    messageId: undefined,
    messageType: undefined
  };
}

export default Main;
